import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { EndpointFactory } from 'src/app/shared/services/endpoint.service';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from 'src/app/shared/services/auth.service';

export enum TopicStatus {
  NEW = 'NEW',
  ADMIN_REVIEW = 'ADMIN_REVIEW',
  SENT_TO_CUSTOMER = 'SENT_TO_CUSTOMER',
  RESOLVED = 'RESOLVED'
}

export interface Topic {
  id: string;
  title: string;
  projectId: string;
  userId: string;
  created: string;
  lastUpdated: string;
  members: string[];
  messages: TopicMessage[];
  status: TopicStatus;
  clientId: string;
}

export enum MessageStatus {
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  READ = 'READ',
  FAILED = 'FAILED'
}

export enum MessageRole {
  DRAFTER = 'Drafter',
  ADMIN = 'Admin',
  CUSTOMER_ADMIN = 'CustomerAdmin'
}

export interface TopicMessage {
  id: string;
  topicStatus: TopicStatus;
  authorId: string;
  authorName: string;
  allowedRoles: MessageRole[];
  members: string[];
  content: string;
  created: string;
  imageUrls: string[];
  attachments: TopicAttachment[];
  status: MessageStatus;
  role: MessageRole;
  discussionId: string;
  projectId: string;
}

export interface TopicAttachment {
  url: string;
  fileName: string;
  fileType: string;
  size: number;
}

export interface TopicMessageRequestDTO {
  topicStatus: TopicStatus;
  authorId: string;
  authorName: string;
  allowedRoles: MessageRole[];
  members: string[];
  content: string;
  imageUrls: string[];
  attachments: TopicAttachment[];
  role: MessageRole;
  id: string;
  projectId: string;
}

export enum ProjectCommunicationStatus {
  NEW = 'NEW',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED'
}

export interface Project {
  id: string;
  title: string;
  description: string;
  creatorId: string;
  created: string;
  lastUpdated: string;
  status: ProjectCommunicationStatus;
  members: string[];
  tags: string[];
  clientId: string | null;
  imageUrl: string;
  containsUnreadMessages: boolean;
}

export interface ImageUploadResponse {
  isSuccess: boolean;
  result: {
    fileName: string;
    url: string;
    contentType: string;
    size: number;
    uploadedAt: string;
  };
  messages: string[];
}

export interface Customer {
  id: string;
  name: string;
  email: string;
  role: number;
  active: boolean;
  clientId: string;
  permissions: string[] | null;
}

@Injectable({
  providedIn: 'root'
})
export class TopicService {
  private baseUrl = 'api/communication';

  constructor(
    private http: HttpClient,
    private endpointFactory: EndpointFactory,
    private authService: AuthService
  ) {}

  private toServerRole(role: MessageRole): string {
    const map: Record<MessageRole, string> = {
      [MessageRole.DRAFTER]: 'Drafter',
      [MessageRole.ADMIN]: 'Admin',
      [MessageRole.CUSTOMER_ADMIN]: 'CustomerAdmin'
    };
    return map[role];
  }

  getTopic(topicId: string | null): Observable<Topic> {
    return this.http.get<Topic>(`${this.baseUrl}/topic/${topicId ?? 'null'}`);
  }

  createTopic(title: string, members: string[], projectId: string): Observable<Topic> {
    const payload = {
      title: title,
      members: members
    };

    const url = this.endpointFactory.CreateEmptyTopic.replace(':id', projectId);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.post<any>(url, payload, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to create topic');
      })
    );
  }

  getMessages(topic: Topic): TopicMessage[] {
    return topic.messages;
  }

  getTopicMessages(projectId: string, topicId: string): Observable<Topic> {
    const url = this.endpointFactory.GetUnresolvedTopicsByProjectForUser.replace(':projectId', projectId);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<any>(url, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          const topic = response.result.find((t: Topic) => t.id === topicId);
          if (!topic) {
            throw new Error('Topic not found');
          }
          return topic;
        }
        throw new Error('Failed to fetch topic messages');
      })
    );
  }

  private getDefaultAllowedRoles(): MessageRole[] {
    return [MessageRole.DRAFTER, MessageRole.ADMIN];
  }

  sendMessage(messageDTO: TopicMessageRequestDTO): Observable<any> {
    const url = this.endpointFactory.AddTopicMessage
      .replace(':projectId', messageDTO.projectId)
      .replace(':id', messageDTO.id);

    const requestBody: any = {
      topicStatus: messageDTO.topicStatus,
      authorId: messageDTO.authorId,
      allowedRoles: messageDTO.allowedRoles.map(role => this.toServerRole(role)),
      authorName: messageDTO.authorName,
      members: messageDTO.members,
      content: messageDTO.content
    };

    if (messageDTO.attachments && messageDTO.attachments.length > 0) {
      requestBody.attachments = messageDTO.attachments;
    }

    if (messageDTO.imageUrls && messageDTO.imageUrls.length > 0) {
      requestBody.imageUrls = messageDTO.imageUrls;
    }

    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.post<any>(url, requestBody, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to send message');
      })
    );
  }


  getAllTopics(): Observable<Topic[]> {
    return this.http.get<any>(`${this.baseUrl}/topics`).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to fetch topics');
      })
    );
  }

  getTopicsByProjectId(projectId: string): Observable<Topic[]> {
    const url = this.endpointFactory.GetUnresolvedTopicsByProjectForUser.replace(':projectId', projectId);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<any>(url, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to fetch topics');
      })
    );
  }

  uploadFile(file: File): Observable<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('container', 'test');

    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.post<ImageUploadResponse>(this.endpointFactory.UploadFile, formData, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response;
        }
        throw new Error('Failed to upload file');
      }),
      catchError(error => {
        console.error('File upload error:', error);
        return throwError(() => new Error('Failed to upload file'));
      })
    );
  }

  uploadImage(file: File): Observable<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<ImageUploadResponse>(`${this.baseUrl}/api/files/image`, formData).pipe(
      map(response => {
        if (response.isSuccess) {
          return response;
        }
        throw new Error('Failed to upload image');
      }),
      catchError(error => {
        console.error('Image upload error:', error);
        return throwError(() => new Error('Failed to upload image'));
      })
    );
  }

  uploadAttachment(file: File, topicId: string): Observable<TopicAttachment> {
    return this.uploadFile(file).pipe(
      map(response => {
        if (response.isSuccess) {
          return {
            url: response.result.url,
            fileName: response.result.fileName,
            fileType: response.result.contentType,
            size: response.result.size
          };
        }
        throw new Error('Failed to upload attachment');
      }),
      catchError(error => {
        console.error('Attachment upload error:', error);
        return throwError(() => new Error('Failed to upload attachment'));
      })
    );
  }

  downloadAttachment(attachment: TopicAttachment): void {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  //getNewMessages(): Observable<TopicMessage> {
   // return this.http.get<TopicMessage>(`${this.baseUrl}/messages/new`);
  //}

  getAllProjects(): Observable<Project[]> {
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<any>(this.endpointFactory.GetMemberProjectCommunications, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to fetch projects');
      })
    );
  }

  updateTopicMembers(topicId: string, members: string[]): Observable<Topic> {
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.put<Topic>(
      `${this.baseUrl}/topic/${topicId}/members`,
      { members },
      { headers }
    );
  }

  updateProjectStatus(projectId: string, status: ProjectCommunicationStatus): Observable<any> {
    const url = this.endpointFactory.UpdateProjectStatus.replace(':id', projectId);

    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    // Send status as JSON string
    const body = JSON.stringify(status);

    return this.http.patch<any>(url, body, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to update project status');
      })
    );
  }

  updateTopicMessage(projectId: string, topicId: string, messageId: string, message: TopicMessage): Observable<any> {
    const url = this.endpointFactory.UpdateTopicMessage
      .replace(':projectId', projectId)
      .replace(':id', topicId)
      .replace(':messageid', messageId);

    return this.http.patch<any>(url, message).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to update topic message');
      })
    );
  }

  updateTopicStatus(projectId: string, topicId: string, status: TopicStatus): Observable<any> {
    const url = this.endpointFactory.UpdateTopicStatus
      .replace(':projectId', projectId)
      .replace(':id', topicId);

    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    // Send status as JSON string: "RESOLVED"
    const body = JSON.stringify(status);

    return this.http.patch<any>(url, body, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to update topic status');
      })
    );
  }

  updateTopicTitle(projectId: string, topicId: string, title: string): Observable<any> {
    const url = this.endpointFactory.UpdateTopicTitle
      .replace(':projectId', projectId)
      .replace(':id', topicId);

    return this.http.patch<any>(url, { title }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to update topic title');
      })
    );
  }

  getCustomersByClientId(clientId: string): Observable<Customer[]> {
    const url = `${this.endpointFactory.GetUsersByClientId}?clientId=${clientId}`;
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    return this.http.get<any>(url, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to fetch customers');
      })
    );
  }

  updateMessage(message: TopicMessage, projectId: string): Observable<TopicMessage> {
    const url = this.endpointFactory.UpdateTopicMessage
      .replace(':projectId', projectId)
      .replace(':id', message.discussionId)
      .replace(':messageid', message.id);

    // Get the auth token and create headers
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);

    // Get the default roles and add CustomerAdmin if there are members
    const defaultRoles = this.getDefaultAllowedRoles();
    const messageRoles = (message.allowedRoles || []).filter(role => !!role);

    // Add CustomerAdmin role if there are members
    if (message.members && message.members.length > 0) {
      messageRoles.push(MessageRole.CUSTOMER_ADMIN);
    }

    // Combine roles and convert them to the server format
    const allRoles = [...defaultRoles, ...messageRoles];
    const allowedRoles = [...new Set(
      allRoles
        .map(role => this.toServerRole(role))
        .filter(role => !!role)
    )];

    // Ensure topicStatus is not null and has a valid value
    const topicStatus = message.topicStatus || TopicStatus.NEW;

    // Ensure members is always an array
    const members = Array.isArray(message.members) ? message.members : [];

    // Format the request body to match the API expectations
    const requestBody = {
      topicStatus,
      authorId: message.authorId,
      authorName: message.authorName,
      allowedRoles,
      members,
      content: message.content || '',
      imageUrls: message.imageUrls || [],
      attachments: message.attachments || []
    };

    return this.http.patch<any>(url, requestBody, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to update message');
      })
    );
  }

  deleteAttachment(message: TopicMessage, attachment: TopicAttachment): Observable<TopicMessage> {
    const updatedMessage = {
      ...message,
      attachments: message.attachments.filter(a => a.url !== attachment.url)
    };
    return this.updateMessage(updatedMessage, message.projectId);
  }

  deleteImage(message: TopicMessage, imageUrl: string): Observable<TopicMessage> {
    const updatedMessage = {
      ...message,
      imageUrls: message.imageUrls.filter(url => url !== imageUrl)
    };
    return this.updateMessage(updatedMessage, message.projectId);
  }

  checkNewMessages(): Observable<{ [topicId: string]: any[] }> {
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    return this.http.get<any>(this.endpointFactory.GetUnreadMessages, { headers }).pipe(
      map(response => {
        if (response?.isSuccess) {
          return response.result;
        }
        return {};
      }),
      catchError(error => {
        console.error('Failed to check new messages:', error);
        return throwError(() => new Error('Failed to check new messages'));
      })
    );
  }

  markTopicMessagesAsVisited(projectId: string, topicId: string): Observable<any> {
    const url = this.endpointFactory.MarkTopicMessagesAsVisited
      .replace(':projectId', projectId)
      .replace(':id', topicId);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    return this.http.patch<any>(url, {}, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to mark messages as visited');
      }),
      catchError(error => {
        console.error('Failed to mark messages as visited:', error);
        return throwError(() => new Error('Failed to mark messages as visited'));
      })
    );
  }

  deleteFileByUrl(fileUrl: string): Observable<any> {
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders().set('Authorization', `Bearer ${token}`);
    const url = `${this.endpointFactory.DeleteFileByUrl}?url=${encodeURIComponent(fileUrl)}`;
    return this.http.delete<any>(url, { headers });
  }

  updateProjectMembers(projectId: string, members: string[]): Observable<any> {
    const url = this.endpointFactory.UpdateProjectStatus.replace(':id', projectId);
    const token = this.authService.getAuthBearerToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });

    return this.http.patch<any>(url, { members }, { headers }).pipe(
      map(response => {
        if (response.isSuccess) {
          return response.result;
        }
        throw new Error('Failed to update project members');
      })
    );
  }
}
